import { Injectable, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

export interface AlertNoti {
  id: number;
  policyNo: string | null;
  insuredCode: string | null;
  productCode: string | null;
  palnCode: string | null;
  alertMsg: string;
  alertToUser: string | null;
  alertToGroup: number | null;
  alertTime: string;
  url: string | null;
  signRequestNo: string | null;
  menuId: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection?: signalR.HubConnection;
  private connectionState = new BehaviorSubject<signalR.HubConnectionState>(
    signalR.HubConnectionState.Disconnected
  );
  
  // Observable for incoming notifications
  private notificationSubject = new Subject<AlertNoti>();
  public notification$ = this.notificationSubject.asObservable();
  
  // Observable for connection state
  public connectionState$ = this.connectionState.asObservable();
  
  private authService = inject(AuthService);

  private userCode?: string;  // Agent code (e.g., "A0001")
  private userTypeId?: number;

  constructor() {
    console.log('[SignalR] Service initialized');
  }

  /**
   * Start SignalR connection
   * @param userCode User code (agent code like "A0001")
   * @param userTypeId User type ID for group notifications
   */
  async startConnection(userCode: string, userTypeId: number): Promise<void> {
    // If already connected but with different userCode, disconnect first
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      if (this.userCode === userCode) {
        console.log('[SignalR] Already connected with same userCode:', userCode);
        return;
      }
      
      console.log(`[SignalR] UserCode changed from ${this.userCode} to ${userCode}, reconnecting...`);
      await this.stopConnection();
    }

    this.userCode = userCode;
    this.userTypeId = userTypeId;

    // Use pdfHost (base URL) instead of apiUrl which has /api/i4u path
    // SignalR hub is at root: /notifyhub
    const hubUrl = `${environment.pdfHost}/notifyhub`;
    console.log('[SignalR] Connecting to:', hubUrl);

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | 
                   signalR.HttpTransportType.ServerSentEvents | 
                   signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext: any) => {
          // Exponential backoff: 0s, 2s, 5s, 10s, 20s, 30s
          const delays = [0, 2000, 5000, 10000, 20000, 30000];
          const delay = delays[Math.min(retryContext.previousRetryCount, delays.length - 1)];
          console.log(`[SignalR] Retry attempt ${retryContext.previousRetryCount + 1} in ${delay}ms`);
          return delay;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Register event handlers
    this.registerEventHandlers();

    try {
      console.log('[SignalR] Starting connection...');
      await this.hubConnection.start();
      this.connectionState.next(this.hubConnection.state);
      console.log('[SignalR] Connected successfully. State:', this.hubConnection.state);
      console.log('[SignalR] Connection ID:', this.hubConnection.connectionId);
      
      // Register user and join group
      await this.registerUserAndJoinGroup();
      console.log('[SignalR] Registration complete');
    } catch (err: any) {
      console.error('[SignalR] Connection failed:', err);
      console.error('[SignalR] Error message:', err?.message);
      console.error('[SignalR] Error stack:', err?.stack);
      console.error('[SignalR] Error details:', JSON.stringify(err, null, 2));
      this.connectionState.next(signalR.HubConnectionState.Disconnected);
      throw err;
    }
  }

  /**
   * Register event handlers for SignalR connection
   */
  private registerEventHandlers(): void {
    if (!this.hubConnection) return;

    // Handle incoming notifications
    this.hubConnection.on('ReceiveNotification', (alert: AlertNoti) => {
      console.log('🔔 [SignalR] ReceiveNotification event triggered!');
      console.log('🔔 [SignalR] Alert object:', JSON.stringify(alert, null, 2));
      console.log('🔔 [SignalR] Emitting to notificationSubject...');
      this.notificationSubject.next(alert);
      console.log('🔔 [SignalR] Emission complete');
    });

    // Handle reconnecting
    this.hubConnection.onreconnecting((error: any) => {
      console.log('[SignalR] Reconnecting...', error?.message);
      this.connectionState.next(signalR.HubConnectionState.Reconnecting);
    });

    // Handle reconnected
    this.hubConnection.onreconnected(async (connectionId: any) => {
      console.log('[SignalR] Reconnected. New connection ID:', connectionId);
      this.connectionState.next(signalR.HubConnectionState.Connected);
      
      // Re-register user and rejoin group after reconnection
      await this.registerUserAndJoinGroup();
    });

    // Handle closed
    this.hubConnection.onclose((error: any) => {
      console.log('[SignalR] Connection closed', error?.message);
      this.connectionState.next(signalR.HubConnectionState.Disconnected);
    });
  }

  /**
   * Register user and join group
   */
  private async registerUserAndJoinGroup(): Promise<void> {
    if (!this.hubConnection || 
        this.hubConnection.state !== signalR.HubConnectionState.Connected ||
        !this.userTypeId) {
      return;
    }

    const connectionId = this.hubConnection.connectionId;
    if (!connectionId) return;

    // Decode JWT to get all fields required by the server's RegisterUser(5 params)
    const decoded = this.authService.decodeToken() ?? {};
    const userId    = decoded?.UserId?.toString() ?? '';
    const userCode  = decoded?.UserCode ?? '';
    const fullname  = decoded?.Fullname ?? '';
    const roleLabel = decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? '';

    if (!userId) {
      console.warn('[SignalR] No UserId in token — skipping RegisterUser');
      return;
    }

    try {
      await this.hubConnection.invoke('RegisterUser', userId, connectionId, userCode, fullname, roleLabel);
      console.log('[SignalR] User registered:', userId, userCode);

      // Join group for group notifications
      const groupId = this.userTypeId.toString();
      await this.hubConnection.invoke('JoinGroup', groupId, connectionId);
      console.log('[SignalR] Joined group:', groupId);
    } catch (err) {
      console.error('[SignalR] Error registering user/joining group:', err);
    }
  }

  /**
   * Stop SignalR connection
   */
  async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      try {
        await this.hubConnection.stop();
        console.log('[SignalR] Connection stopped');
        this.connectionState.next(signalR.HubConnectionState.Disconnected);
      } catch (err) {
        console.error('[SignalR] Error stopping connection:', err);
      }
    }
  }

  /**
   * Get current connection state
   */
  getConnectionState(): signalR.HubConnectionState {
    return this.hubConnection?.state ?? signalR.HubConnectionState.Disconnected;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Get connection ID
   */
  getConnectionId(): string | null {
    return this.hubConnection?.connectionId ?? null;
  }
}
